import { useCallback, useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { chatApi } from '../lib/api.js';
import { useLiveRefresh } from '../lib/live.js';
import { ChatPanel } from './Conversation.js';

const TABS = [
  { to: '/', label: 'Today', icon: 'M4 6h16M4 12h16M4 18h10' },
  { to: '/bookings', label: 'Bookings', icon: 'M8 2v4M16 2v4M3 9h18M5 5h14a2 2 0 012 2v12a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2z' },
  { to: '/people', label: 'People', icon: 'M16 11a4 4 0 10-8 0 4 4 0 008 0zM4 21v-1a6 6 0 0112 0v1M17 11a3 3 0 100-6M20 21v-1a5 5 0 00-3-4.6' },
  { to: '/services', label: 'Services', icon: 'M12 3l9 4-9 4-9-4 9-4zm-9 9l9 4 9-4M3 17l9 4 9-4' },
  { to: '/settings', label: 'Settings', icon: 'M12 15a3 3 0 100-6 3 3 0 000 6zm7-3l2 1-1 3-2-.5a7 7 0 01-1.5 1.5l.5 2-3 1-1-2a7 7 0 01-2 0l-1 2-3-1 .5-2A7 7 0 015 15.5L3 16l-1-3 2-1a7 7 0 010-2l-2-1 1-3 2 .5A7 7 0 016.5 5L6 3l3-1 1 2a7 7 0 012 0l1-2 3 1-.5 2A7 7 0 0117 6.5l2-.5 1 3-2 1a7 7 0 010 2z' },
];
const ID = /^[0-9a-f-]{36}$/i;

/** Unread messages for the bottom bar: every minute, on a message notification, and after reading. */
function useUnread() {
  const [n, setN] = useState(0);
  const load = useCallback(() => {
    chatApi.unread().then((r) => setN(r.unread), () => undefined);
  }, []);
  useLiveRefresh(load, { on: 'messages-changed' });
  useEffect(() => {
    window.addEventListener('rydres:unread', load);
    return () => window.removeEventListener('rydres:unread', load);
  }, [load]);
  return n;
}

/**
 * The bottom bar, and a conversation on top of whatever screen opened it (`?chat=<id>`): messages
 * live in People, so no screen has to navigate away to show one.
 */
export function Layout() {
  const unread = useUnread();
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const chat = params.get('chat');
  const open = chat && ID.test(chat) ? chat : null;
  const close = useCallback(() => {
    // Opened from a button on this screen: Back returns to it. Opened from a notification: drop the parameter.
    if ((location.state as { chatOpened?: boolean } | null)?.chatOpened) navigate(-1);
    else
      setParams(
        (p) => {
          const next = new URLSearchParams(p);
          next.delete('chat');
          return next;
        },
        { replace: true },
      );
  }, [location.state, navigate, setParams]);

  return (
    <div className={`flex min-h-full flex-col ${open ? 'lg:pr-[440px]' : ''}`}>
      <div className="mx-auto flex w-full max-w-3xl flex-1 flex-col">
        <main className="flex-1 px-4 pb-24 pt-4 safe-t">
          <Outlet />
        </main>
      </div>
      <nav className={`fixed inset-x-0 bottom-0 z-20 border-t border-line bg-card/95 backdrop-blur safe-b ${open ? 'lg:right-[440px]' : ''}`}>
        <div className="mx-auto flex max-w-3xl justify-around">
          {TABS.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.to === '/'}
              className={({ isActive }) =>
                `flex flex-1 flex-col items-center gap-0.5 py-2 text-[11px] font-bold min-[400px]:text-xs ${isActive ? 'text-accent-text' : 'text-muted'}`
              }
            >
              {({ isActive }) => (
                <>
                  <span className={`relative flex h-7 w-12 items-center justify-center rounded-full ${isActive ? 'bg-brand text-on-brand' : ''}`}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d={t.icon} />
                    </svg>
                    {t.to === '/people' && unread > 0 && (
                      <span className="absolute -top-1 right-0.5 min-w-4 rounded-full bg-danger px-1 text-center text-[10px] font-black leading-4 text-card" aria-label={`${unread} unread`}>
                        {unread > 99 ? '99+' : unread}
                      </span>
                    )}
                  </span>
                  {t.label}
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
      {open && <ChatPanel id={open} onClose={close} />}
    </div>
  );
}
